import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { shouldShowPwaPrompt } from '../lib/pwaPrompt';

const PWAInstallPrompt = () => {
  const { orgShortName, orgName } = useOrganization();
  const location = useLocation();
  const appLabel = orgShortName || orgName || 'MYKLUBB Control';
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Detect if app is already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone || 
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Don't show if already dismissed
      const dismissed = localStorage.getItem('pwa-dismissed');
      if (!dismissed && !standalone) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show prompt if not in standalone and not dismissed
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-dismissed-ios');
      if (!dismissed) {
        setShowPrompt(true);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome installation
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        // User accepted the install prompt
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    
    // Remember dismissal
    if (isIOS) {
      localStorage.setItem('pwa-dismissed-ios', 'true');
    } else {
      localStorage.setItem('pwa-dismissed', 'true');
    }
  };

  // Don't show if already in standalone mode ni en pantallas de auth
  if (isStandalone || !showPrompt || !shouldShowPwaPrompt(location.pathname)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 bg-black border-2 border-gray-300 rounded-2xl p-4 shadow-lg z-50 max-w-sm mx-auto">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0 bg-gray-100 rounded-lg p-2">
          <Download size={20} className="text-black" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-bold text-sm mb-1">
            Instalar {appLabel}
          </h3>
          <p className="text-gray-300 text-xs mb-3">
            {isIOS 
              ? 'Toca el botón "Compartir" y selecciona "Agregar a pantalla de inicio"'
              : 'Instala la app para acceso rápido y mejor experiencia'
            }
          </p>
          
          <div className="flex space-x-2">
            {!isIOS && (
              <button
                onClick={handleInstallClick}
                className="bg-white text-black px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-100 transition-all"
              >
                Instalar
              </button>
            )}
            <button
              onClick={handleDismiss}
              className="text-gray-300 hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
            >
              Ahora no
            </button>
          </div>
        </div>
        
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 text-gray-400 hover:text-white transition-all"
        >
          <X size={16} />
        </button>
      </div>
      
      {/* iOS specific instructions */}
      {isIOS && (
        <div className="mt-3 pt-3 border-t border-gray-600">
          <div className="flex items-center space-x-2 text-xs text-gray-400">
            <span>App</span>
            <span>Safari → Compartir → Agregar a pantalla de inicio</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;

